import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import { branding } from '../constants/branding';
import { igamingService } from '../services/igamingService';
import { GameBalanceService } from '../services/gameBalanceService';
import { FaSpinner, FaTimes, FaRedo } from 'react-icons/fa';
import '../App.css';

interface GameState {
  gameId: number;
  gameName: string;
  gameType: 'blackjack' | 'roulette' | 'slots';
  sessionId: string;
  balance: number;
  bet: number;
  status: 'waiting' | 'playing' | 'finished';
  result?: any;
}

const GamePlayer: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [gameData, setGameData] = useState<any>(null);

  useEffect(() => {
    const loadGame = async () => {
      if (!gameId) {
        setError('Game ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // First, get the game details
        const gamesResponse = await igamingService.getCasinoGames({ page: 0, size: 100 });
        const game = gamesResponse.content.find(g => g.id === Number(gameId));
        
        if (!game) {
          setError('Game not found');
          setLoading(false);
          return;
        }

        setGameData(game);
        
        // Create session
        const session = await igamingService.launchGame(Number(gameId));
        
        // Determine game type from game name or category
        const gameName = game.name || 'Game';
        const gameNameLower = gameName.toLowerCase();
        let gameType: 'blackjack' | 'roulette' | 'slots' = 'slots';
        if (gameNameLower.includes('blackjack')) {
          gameType = 'blackjack';
        } else if (gameNameLower.includes('roulette') || gameNameLower.includes('roulet')) {
          gameType = 'roulette';
        }

        // Fetch iGaming account balance
        let balance = 0;
        try {
          const balanceResponse = await igamingService.getIGamingBalance();
          balance = balanceResponse.balance || 0;
        } catch (err) {
          console.error('Failed to fetch iGaming balance:', err);
          // Default to 0 if balance fetch fails
          balance = 0;
        }

        setGameState({
          gameId: Number(gameId),
          gameName: gameName,
          gameType,
          sessionId: session.sessionId,
          balance: balance,
          bet: 0,
          status: 'waiting'
        });
      } catch (err: any) {
        setError(err.message || 'Failed to load game');
      } finally {
        setLoading(false);
      }
    };

    loadGame();
  }, [gameId]);

  if (loading) {
    return (
      <PageTransition>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <FaSpinner className="animate-spin" style={{ fontSize: 48, color: branding.primaryColor }} />
        </div>
      </PageTransition>
    );
  }

  if (error || !gameState) {
    return (
      <PageTransition>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
          <h2 style={{ fontSize: 24, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
            {error || 'Game not found'}
          </h2>
          <button
            onClick={() => navigate('/igaming')}
            style={{
              padding: '12px 24px',
              background: branding.primaryColor,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              marginTop: 16
            }}
          >
            Back to Games
          </button>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          background: '#ffffff',
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden'
        }}>
          {/* Game Header */}
          <div style={{
            padding: '20px',
            background: '#1f2937',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>{gameState.gameName}</h1>
              <p style={{ fontSize: 14, margin: '4px 0 0 0', opacity: 0.8 }}>Session: {gameState.sessionId.substring(0, 8)}...</p>
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, opacity: 0.8 }}>Balance</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>€{gameState.balance.toFixed(2)}</div>
              </div>
              <button
                onClick={() => navigate('/igaming')}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <FaTimes /> Close
              </button>
            </div>
          </div>

          {/* Game Content */}
          <div style={{ padding: '40px', minHeight: '500px' }}>
            {gameState.gameType === 'blackjack' && (
              <BlackjackGame gameState={gameState} setGameState={setGameState} />
            )}
            {gameState.gameType === 'roulette' && (
              <RouletteGame gameState={gameState} setGameState={setGameState} />
            )}
            {gameState.gameType === 'slots' && (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🎰</div>
                <h3 style={{ fontSize: 20, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                  Slots Coming Soon
                </h3>
                <p style={{ fontSize: 14, color: '#6b7280' }}>
                  Slot games will be available soon
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

// Blackjack Game Component
const BlackjackGame: React.FC<{ gameState: GameState; setGameState: React.Dispatch<React.SetStateAction<GameState | null>> }> = ({ gameState, setGameState }) => {
  const [playerHand, setPlayerHand] = useState<number[]>([]);
  const [dealerHand, setDealerHand] = useState<number[]>([]);
  const [bet, setBet] = useState(0);
  const [gameStatus, setGameStatus] = useState<'betting' | 'playing' | 'dealer-turn' | 'finished'>('betting');
  const [message, setMessage] = useState('');
  const [splitHand, setSplitHand] = useState<number[] | null>(null);
  const [activeSplitHand, setActiveSplitHand] = useState<'first' | 'second'>('first');
  const [splitBet, setSplitBet] = useState(0);
  const [hasSplit, setHasSplit] = useState(false);

  const getCardValue = (card: number): number => {
    if (card === 1) return 11; // Ace
    if (card > 10) return 10; // Face cards
    return card;
  };

  const calculateHand = (hand: number[]): number => {
    let total = 0;
    let aces = 0;
    hand.forEach(card => {
      if (card === 1) aces++;
      total += getCardValue(card);
    });
    while (total > 21 && aces > 0) {
      total -= 10;
      aces--;
    }
    return total;
  };

  const dealCard = (): number => {
    return Math.floor(Math.random() * 13) + 1;
  };

  const startGame = async () => {
    if (bet <= 0 || bet > gameState.balance) {
      setMessage('Invalid bet amount');
      return;
    }

    try {
      // Place bet via API
      const newBalance = await GameBalanceService.placeBet(bet);
      
      const newPlayerHand = [dealCard(), dealCard()];
      const newDealerHand = [dealCard(), dealCard()];

      setPlayerHand(newPlayerHand);
      setDealerHand(newDealerHand);
      setGameStatus('playing');
      setMessage('');
      
      // Update balance from API response
      setGameState(prev => prev ? { ...prev, balance: newBalance } : null);

      // Check for blackjack
      if (calculateHand(newPlayerHand) === 21) {
        setGameStatus('finished');
        setMessage('Blackjack! You win!');
        const finalBalance = await GameBalanceService.handleBlackjackResult(bet, 'blackjack');
        setGameState(prev => prev ? { ...prev, balance: finalBalance, status: 'finished' } : null);
      }
    } catch (error: any) {
      setMessage(`Error placing bet: ${error.message || 'Please try again'}`);
    }
  };

  const canSplit = (): boolean => {
    if (hasSplit || playerHand.length !== 2) return false;
    const [card1, card2] = playerHand;
    // Cards can be split if they have the same value (e.g., two 8s, or a 10 and a King)
    return getCardValue(card1) === getCardValue(card2) && bet <= gameState.balance;
  };

  const split = () => {
    if (!canSplit() || bet > gameState.balance) {
      setMessage('Cannot split - insufficient balance or invalid hand');
      return;
    }

    // Split the hand
    const [card1, card2] = playerHand;
    setPlayerHand([card1, dealCard()]); // First hand gets one new card
    setSplitHand([card2, dealCard()]); // Second hand gets one new card
    setSplitBet(bet); // Match the original bet
    setHasSplit(true);
    setActiveSplitHand('first');
    setMessage('Playing first hand...');
  };

  const hit = async () => {
    const newCard = dealCard();
    
    if (hasSplit && activeSplitHand === 'first') {
      const newHand = [...playerHand, newCard];
      setPlayerHand(newHand);

      const total = calculateHand(newHand);
      if (total > 21) {
        setMessage('First hand busts! Playing second hand...');
        setActiveSplitHand('second');
      } else if (total === 21) {
        setMessage('First hand: 21! Playing second hand...');
        setActiveSplitHand('second');
      }
    } else if (hasSplit && activeSplitHand === 'second') {
      const newHand = [...(splitHand || []), newCard];
      setSplitHand(newHand);

      const total = calculateHand(newHand);
      if (total > 21 || total === 21) {
        stand(newHand);
      }
    } else {
      // Normal single hand play
      const newHand = [...playerHand, newCard];
      setPlayerHand(newHand);

      const total = calculateHand(newHand);
      if (total > 21) {
        setGameStatus('finished');
        setMessage('Bust! You lose.');
        try {
          const finalBalance = await GameBalanceService.handleBlackjackResult(bet, 'lose');
          setGameState(prev => prev ? { ...prev, balance: finalBalance, status: 'finished' } : null);
        } catch (error: any) {
          setMessage(`Bust! Error settling: ${error.message}`);
        }
      } else if (total === 21) {
        stand(newHand);
      }
    }
  };

  const stand = async (currentPlayerHand?: number[]) => {
    if (hasSplit && activeSplitHand === 'first') {
      setMessage('First hand stands. Playing second hand...');
      setActiveSplitHand('second');
      return;
    }

    setGameStatus('dealer-turn');
    setTimeout(async () => {
      let newDealerHand = [...dealerHand];
      while (calculateHand(newDealerHand) < 17) {
        newDealerHand.push(dealCard());
      }
      setDealerHand(newDealerHand);

      const dealerTotal = calculateHand(newDealerHand);
      const dealerBust = dealerTotal > 21;

      setGameStatus('finished');

      try {
        if (hasSplit && splitHand) {
          // Calculate results for both hands
          const hand1Total = calculateHand(currentPlayerHand || playerHand);
          const hand2Total = calculateHand(splitHand);
          const hand1Bust = hand1Total > 21;
          const hand2Bust = hand2Total > 21;

          let totalWinnings = 0;
          let messages: string[] = [];

          // Hand 1 result
          if (hand1Bust) {
            messages.push('Hand 1: Bust (-€' + bet + ')');
            totalWinnings -= bet;
          } else if (dealerBust || hand1Total > dealerTotal) {
            messages.push('Hand 1: Win (+€' + bet + ')');
            totalWinnings += bet;
          } else if (hand1Total < dealerTotal) {
            messages.push('Hand 1: Lose (-€' + bet + ')');
            totalWinnings -= bet;
          } else {
            messages.push('Hand 1: Push');
          }

          // Hand 2 result
          if (hand2Bust) {
            messages.push('Hand 2: Bust (-€' + splitBet + ')');
            totalWinnings -= splitBet;
          } else if (dealerBust || hand2Total > dealerTotal) {
            messages.push('Hand 2: Win (+€' + splitBet + ')');
            totalWinnings += splitBet;
          } else if (hand2Total < dealerTotal) {
            messages.push('Hand 2: Lose (-€' + splitBet + ')');
            totalWinnings -= splitBet;
          } else {
            messages.push('Hand 2: Push');
          }

          setMessage(messages.join(' | ') + ` | Total: ${totalWinnings >= 0 ? '+' : ''}€${totalWinnings}`);
          
          // Handle result via API
          const totalBetAmount = bet + splitBet;
          let finalBalance: number;
          if (totalWinnings > 0) {
            finalBalance = await GameBalanceService.creditWinnings(totalWinnings);
          } else if (totalWinnings < 0) {
            finalBalance = await GameBalanceService.recordLoss(Math.abs(totalWinnings));
          } else {
            // Push - return bet
            finalBalance = await GameBalanceService.creditWinnings(totalBetAmount);
          }
          setGameState(prev => prev ? { ...prev, balance: finalBalance, status: 'finished' } : null);
        } else {
          // Single hand result
          const playerTotal = calculateHand(currentPlayerHand || playerHand);

          let finalBalance: number;
          if (dealerTotal > 21) {
            setMessage('Dealer busts! You win!');
            finalBalance = await GameBalanceService.handleBlackjackResult(bet, 'win');
          } else if (playerTotal > dealerTotal) {
            setMessage('You win!');
            finalBalance = await GameBalanceService.handleBlackjackResult(bet, 'win');
          } else if (playerTotal < dealerTotal) {
            setMessage('Dealer wins!');
            finalBalance = await GameBalanceService.handleBlackjackResult(bet, 'lose');
          } else {
            setMessage('Push! It\'s a tie.');
            finalBalance = await GameBalanceService.handleBlackjackResult(bet, 'push');
          }
          setGameState(prev => prev ? { ...prev, balance: finalBalance, status: 'finished' } : null);
        }
      } catch (error: any) {
        setMessage(`Error settling game: ${error.message || 'Please refresh'}`);
      }
    }, 500);
  };

  const resetGame = () => {
    setPlayerHand([]);
    setDealerHand([]);
    setBet(0);
    setGameStatus('betting');
    setMessage('');
    setSplitHand(null);
    setActiveSplitHand('first');
    setSplitBet(0);
    setHasSplit(false);
    setGameState(prev => prev ? { ...prev, status: 'waiting' } : null);
  };

  const renderCard = (value: number, hidden: boolean = false) => {
    if (hidden) {
      return (
        <div style={{
          width: 80,
          height: 112,
          background: '#1f2937',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: 24,
          fontWeight: 700,
          border: '2px solid #374151'
        }}>
          ?
        </div>
      );
    }

    const suits = ['♠', '♥', '♦', '♣'];
    const suit = suits[Math.floor(Math.random() * 4)];
    const isRed = suit === '♥' || suit === '♦';
    const displayValue = value === 1 ? 'A' : value === 11 ? 'J' : value === 12 ? 'Q' : value === 13 ? 'K' : value;

    return (
      <div style={{
        width: 80,
        height: 112,
        background: '#fff',
        borderRadius: 8,
        border: '2px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: isRed ? '#dc2626' : '#1f2937',
        fontSize: 24,
        fontWeight: 700,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div>{displayValue}</div>
        <div style={{ fontSize: 32 }}>{suit}</div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Betting Phase */}
      {gameStatus === 'betting' && (
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 16 }}>Place Your Bet</h2>
          
          {/* Chip Selection - Same as Roulette */}
          <div style={{ background: '#1f2937', padding: '16px', borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.1)', marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#fff' }}>Select Chip Value</label>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              {[1, 5, 10, 25, 50, 100].map(value => (
                <button
                  key={value}
                  onClick={() => setBet(value)}
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: bet === value 
                      ? `linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)`
                      : value >= 50
                        ? `linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)`
                        : value >= 10
                          ? `linear-gradient(135deg, #10b981 0%, #059669 100%)`
                          : `linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)`,
                    border: bet === value ? '4px solid #fbbf24' : '3px solid rgba(255,255,255,0.3)',
                    color: '#fff',
                    fontSize: 16,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: bet === value ? '0 0 20px rgba(251, 191, 36, 0.5)' : '0 4px 6px rgba(0,0,0,0.3)',
                    transform: bet === value ? 'scale(1.1)' : 'scale(1)',
                    transition: 'all 0.2s'
                  }}
                >
                  <span style={{ fontSize: 10 }}>€</span>
                  <span>{value}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Current Bet Display */}
          <div style={{ background: '#f9fafb', padding: '12px 16px', borderRadius: 8, marginBottom: 24, display: 'inline-block' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#6b7280' }}>Your Bet: </span>
            <span style={{ fontSize: 18, fontWeight: 700, color: '#1f2937' }}>€{bet.toFixed(2)}</span>
          </div>

          {/* Deal Button */}
          <div>
            <button
              onClick={startGame}
              disabled={bet <= 0}
              style={{
                padding: '16px 48px',
                background: bet > 0 ? branding.primaryColor : '#9ca3af',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 20,
                fontWeight: 700,
                cursor: bet > 0 ? 'pointer' : 'not-allowed',
                boxShadow: bet > 0 ? '0 4px 12px rgba(134, 25, 143, 0.4)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              Deal Cards (€{bet.toFixed(2)})
            </button>
          </div>
        </div>
      )}

      {/* Playing Phase */}
      {gameStatus !== 'betting' && (
        <>
          {/* Dealer Hand */}
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>
              Dealer {gameStatus === 'dealer-turn' || gameStatus === 'finished' ? `(${calculateHand(dealerHand)})` : ''}
            </h3>
            <div style={{ display: 'flex', gap: 12 }}>
              {dealerHand.map((card, idx) => 
                renderCard(card, idx === 1 && gameStatus === 'playing')
              )}
            </div>
          </div>

          {/* Player Hand(s) */}
          <div>
            {hasSplit && splitHand ? (
              // Show both hands when split
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                {/* First Hand */}
                <div style={{
                  padding: '16px',
                  borderRadius: 12,
                  border: activeSplitHand === 'first' ? '3px solid #10b981' : '2px solid #e5e7eb',
                  background: activeSplitHand === 'first' ? '#f0fdf4' : '#fff'
                }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                    Hand 1 ({calculateHand(playerHand)}) {activeSplitHand === 'first' && '← Active'}
                  </h3>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {playerHand.map((card, idx) => renderCard(card))}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, textAlign: 'center', color: '#6b7280' }}>
                    Bet: €{bet}
                  </div>
                </div>

                {/* Second Hand */}
                <div style={{
                  padding: '16px',
                  borderRadius: 12,
                  border: activeSplitHand === 'second' ? '3px solid #10b981' : '2px solid #e5e7eb',
                  background: activeSplitHand === 'second' ? '#f0fdf4' : '#fff'
                }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                    Hand 2 ({calculateHand(splitHand)}) {activeSplitHand === 'second' && '← Active'}
                  </h3>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {splitHand.map((card, idx) => renderCard(card))}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, textAlign: 'center', color: '#6b7280' }}>
                    Bet: €{splitBet}
                  </div>
                </div>
              </div>
            ) : (
              // Show single hand
              <>
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>
                  Your Hand ({calculateHand(playerHand)})
                </h3>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {playerHand.map((card, idx) => renderCard(card))}
                </div>
              </>
            )}
          </div>

          {/* Message */}
          {message && (
            <div style={{
              padding: '16px',
              background: message.includes('win') ? '#d1fae5' : message.includes('lose') ? '#fee2e2' : '#fef3c7',
              borderRadius: 8,
              textAlign: 'center',
              fontSize: 18,
              fontWeight: 600,
              color: message.includes('win') ? '#065f46' : message.includes('lose') ? '#991b1b' : '#92400e'
            }}>
              {message}
            </div>
          )}

          {/* Game Actions */}
          {gameStatus === 'playing' && (
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={hit}
                style={{
                  padding: '12px 24px',
                  background: '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Hit
              </button>
              {canSplit() && (
                <button
                  onClick={split}
                  style={{
                    padding: '12px 24px',
                    background: '#f59e0b',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 0 10px rgba(245, 158, 11, 0.5)'
                  }}
                >
                  Split (€{bet})
                </button>
              )}
              <button
                onClick={() => stand()}
                style={{
                  padding: '12px 24px',
                  background: '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Stand
              </button>
            </div>
          )}

          {/* Reset Button */}
          {gameStatus === 'finished' && (
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={resetGame}
                style={{
                  padding: '12px 24px',
                  background: branding.primaryColor,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <FaRedo /> Play Again
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Roulette Bet Interface
interface RouletteBet {
  type: 'red' | 'black' | 'number' | 'even' | 'odd' | 'split' | 'street' | 'corner' | 'line' | 'dozen1' | 'dozen2' | 'dozen3' | '1-18' | '19-36' | 'col1' | 'col2' | 'col3';
  value: number | string | number[]; // Can be single number, string, or array of numbers for splits/corners/etc
  amount: number;
}

// Roulette Game Component
const RouletteGame: React.FC<{ gameState: GameState; setGameState: React.Dispatch<React.SetStateAction<GameState | null>> }> = ({ gameState, setGameState }) => {
  const [selectedChip, setSelectedChip] = useState(10);
  const [bets, setBets] = useState<RouletteBet[]>([]);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [lastBets, setLastBets] = useState<RouletteBet[]>([]);

  const chipValues = [1, 5, 10, 25, 50, 100];
  const numbers = Array.from({ length: 37 }, (_, i) => i); // 0-36
  const redNumbers = [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36];
  const blackNumbers = [2, 4, 6, 8, 10, 11, 13, 15, 17, 20, 22, 24, 26, 28, 29, 31, 33, 35];
  
  const totalBetAmount = bets.reduce((sum, bet) => sum + bet.amount, 0);

  const handlePlaceBet = (
    betType: 'red' | 'black' | 'number' | 'even' | 'odd' | 'split' | 'street' | 'corner' | 'line' | 'dozen1' | 'dozen2' | 'dozen3' | '1-18' | '19-36' | 'col1' | 'col2' | 'col3',
    value: number | string | number[]
  ) => {
    if (spinning || result !== null) return;

    if (selectedChip > gameState.balance || totalBetAmount + selectedChip > gameState.balance) {
      setMessage('Insufficient balance');
      return;
    }

    // For array values, we need to compare by content, not reference
    const existingBetIndex = bets.findIndex(b => {
      if (b.type !== betType) return false;
      if (Array.isArray(value) && Array.isArray(b.value)) {
        return JSON.stringify(b.value.sort()) === JSON.stringify(value.sort());
      }
      return b.value === value;
    });

    if (existingBetIndex >= 0) {
      const newBets = [...bets];
      newBets[existingBetIndex].amount += selectedChip;
      setBets(newBets);
    } else {
      setBets([...bets, { type: betType, value, amount: selectedChip }]);
    }

    const displayValue = Array.isArray(value) ? value.join(',') : value;
    setMessage(`€${selectedChip} placed on ${betType} (${displayValue})`);
  };

  const handleClearBets = () => {
    setBets([]);
    setMessage('All bets cleared');
  };

  const handleRepeatBets = () => {
    if (lastBets.length === 0) {
      setMessage('No previous bets to repeat');
      return;
    }
    const totalAmount = lastBets.reduce((sum, bet) => sum + bet.amount, 0);
    if (totalAmount > gameState.balance) {
      setMessage('Insufficient balance to repeat bets');
      return;
    }
    setBets([...lastBets]);
    setMessage('Previous bets repeated');
  };

  const getBetAmountForNumber = (num: number): number => {
    return bets.filter(b => b.type === 'number' && b.value === num).reduce((sum, b) => sum + b.amount, 0);
  };

  const getBetAmountForType = (type: string): number => {
    return bets.filter(b => b.type === type).reduce((sum, b) => sum + b.amount, 0);
  };

  const getBetAmountForNumbers = (numbers: number[]): number => {
    return bets.filter(b => 
      Array.isArray(b.value) && 
      JSON.stringify(b.value.sort()) === JSON.stringify(numbers.sort())
    ).reduce((sum, b) => sum + b.amount, 0);
  };

  const spin = async () => {
    if (bets.length === 0) {
      setMessage('Please place at least one bet');
      return;
    }

    if (totalBetAmount > gameState.balance) {
      setMessage('Insufficient balance');
      return;
    }

    try {
      // Place bet via API
      const newBalance = await GameBalanceService.placeBet(totalBetAmount);
      setGameState(prev => prev ? { ...prev, balance: newBalance } : null);

      setSpinning(true);
      setMessage('');
      setLastBets(bets);

      setTimeout(async () => {
        const winningNumber = Math.floor(Math.random() * 37);
        setResult(winningNumber);
        setSpinning(false);

        let totalPayout = 0;

      bets.forEach(bet => {
        let won = false;
        let payoutMultiplier = 0;

        if (bet.type === 'number' && bet.value === winningNumber) {
          won = true;
          payoutMultiplier = 36; // 35:1 + original stake
        } else if (bet.type === 'split') {
          // Split bet: 2 adjacent numbers (17:1)
          const numbers = Array.isArray(bet.value) ? bet.value : [];
          if (numbers.includes(winningNumber)) {
            won = true;
            payoutMultiplier = 18; // 17:1 + original stake
          }
        } else if (bet.type === 'street') {
          // Street bet: Row of 3 numbers (11:1)
          const numbers = Array.isArray(bet.value) ? bet.value : [];
          if (numbers.includes(winningNumber)) {
            won = true;
            payoutMultiplier = 12; // 11:1 + original stake
          }
        } else if (bet.type === 'corner') {
          // Corner bet: 4 numbers at intersection (8:1)
          const numbers = Array.isArray(bet.value) ? bet.value : [];
          if (numbers.includes(winningNumber)) {
            won = true;
            payoutMultiplier = 9; // 8:1 + original stake
          }
        } else if (bet.type === 'line') {
          // Line bet: 2 adjacent rows, 6 numbers (5:1)
          const numbers = Array.isArray(bet.value) ? bet.value : [];
          if (numbers.includes(winningNumber)) {
            won = true;
            payoutMultiplier = 6; // 5:1 + original stake
          }
        } else if (bet.type === 'red' && redNumbers.includes(winningNumber)) {
          won = true;
          payoutMultiplier = 2; // 1:1 + original stake
        } else if (bet.type === 'black' && blackNumbers.includes(winningNumber)) {
          won = true;
          payoutMultiplier = 2; // 1:1 + original stake
        } else if (bet.type === 'even' && winningNumber !== 0 && winningNumber % 2 === 0) {
          won = true;
          payoutMultiplier = 2;
        } else if (bet.type === 'odd' && winningNumber !== 0 && winningNumber % 2 === 1) {
          won = true;
          payoutMultiplier = 2;
        } else if (bet.type === 'dozen1' && winningNumber >= 1 && winningNumber <= 12) {
          won = true;
          payoutMultiplier = 3; // 2:1 + original stake
        } else if (bet.type === 'dozen2' && winningNumber >= 13 && winningNumber <= 24) {
          won = true;
          payoutMultiplier = 3;
        } else if (bet.type === 'dozen3' && winningNumber >= 25 && winningNumber <= 36) {
          won = true;
          payoutMultiplier = 3;
        } else if (bet.type === '1-18' && winningNumber >= 1 && winningNumber <= 18) {
          won = true;
          payoutMultiplier = 2;
        } else if (bet.type === '19-36' && winningNumber >= 19 && winningNumber <= 36) {
          won = true;
          payoutMultiplier = 2;
        } else if (bet.type === 'col1' && [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34].includes(winningNumber)) {
          won = true;
          payoutMultiplier = 3;
        } else if (bet.type === 'col2' && [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35].includes(winningNumber)) {
          won = true;
          payoutMultiplier = 3;
        } else if (bet.type === 'col3' && [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36].includes(winningNumber)) {
          won = true;
          payoutMultiplier = 3;
        }

        if (won) {
          totalPayout += bet.amount * payoutMultiplier;
        }
      });

        // Calculate net profit/loss
        // totalPayout already includes the original stake (e.g., 35:1 means you get 36x your bet)
        const netProfit = totalPayout - totalBetAmount;
        const winnings = totalPayout > 0 ? totalPayout - totalBetAmount : 0;
        
        try {
          // Handle result via API
          const finalBalance = await GameBalanceService.handleRouletteResult(totalBetAmount, totalPayout);
          
          if (totalPayout > 0) {
            // Won: Show stake, winnings, and total payout
            setMessage(
              `🎉 Winner! Number ${winningNumber}\n` +
              `💰 Stake: €${totalBetAmount.toFixed(2)} | Winnings: €${winnings.toFixed(2)} | Total Payout: €${totalPayout.toFixed(2)}`
            );
          } else {
            // Lost: Show stake lost
            setMessage(
              `Number ${winningNumber} - Lost\n` +
              `💸 Stake Lost: €${totalBetAmount.toFixed(2)}`
            );
          }
          
          setGameState(prev => prev ? { ...prev, balance: finalBalance, status: 'finished' } : null);
        } catch (error: any) {
          setMessage(`Error settling game: ${error.message || 'Please refresh'}`);
        }
      }, 3000);
    } catch (error: any) {
      setMessage(`Error placing bet: ${error.message || 'Please try again'}`);
      setSpinning(false);
    }
  };

  const resetGame = () => {
    setBets([]);
    setResult(null);
    setMessage('');
    setGameState(prev => prev ? { ...prev, status: 'waiting' } : null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <h2 style={{ fontSize: 28, fontWeight: 700, textAlign: 'center', marginBottom: 16 }}>European Roulette</h2>

      {/* Chip Selection */}
      <div style={{ background: '#1f2937', padding: '16px', borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#fff' }}>Select Chip Value</label>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          {chipValues.map(value => (
            <button
              key={value}
              onClick={() => setSelectedChip(value)}
              style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: selectedChip === value 
                  ? `linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)`
                  : value >= 50 
                    ? `linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)`
                    : value >= 10
                      ? `linear-gradient(135deg, #10b981 0%, #059669 100%)`
                      : `linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)`,
                border: selectedChip === value ? '4px solid #fbbf24' : '3px solid rgba(255,255,255,0.3)',
                color: '#fff',
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: selectedChip === value ? '0 0 20px rgba(251, 191, 36, 0.5)' : '0 4px 6px rgba(0,0,0,0.3)',
                transform: selectedChip === value ? 'scale(1.1)' : 'scale(1)',
                transition: 'all 0.2s'
              }}
            >
              <span style={{ fontSize: 10 }}>€</span>
              <span>{value}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Bet Summary */}
      <div style={{ background: '#f9fafb', padding: '12px 16px', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#6b7280' }}>Total Bets: </span>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#1f2937' }}>€{totalBetAmount.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleRepeatBets}
            disabled={lastBets.length === 0}
            style={{
              padding: '6px 12px',
              background: lastBets.length > 0 ? '#10b981' : '#d1d5db',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: lastBets.length > 0 ? 'pointer' : 'not-allowed'
            }}
          >
            <FaRedo style={{ marginRight: 4, display: 'inline' }} /> Repeat
          </button>
          <button
            onClick={handleClearBets}
            disabled={bets.length === 0}
            style={{
              padding: '6px 12px',
              background: bets.length > 0 ? '#ef4444' : '#d1d5db',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: bets.length > 0 ? 'pointer' : 'not-allowed'
            }}
          >
            <FaTimes style={{ marginRight: 4, display: 'inline' }} /> Clear All
          </button>
        </div>
      </div>

      {/* Roulette Table Container - Full Width on Desktop, Scrollable on Mobile */}
      <div style={{
        width: '100%',
        overflowX: 'auto',
        overflowY: 'visible',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '8px'
      }}>
        {/* Roulette Table - Casino Style */}
        <div style={{
          background: 'linear-gradient(135deg, #0a5f3a 0%, #064e2e 100%)',
          padding: '12px',
          borderRadius: 10,
          border: '4px solid #8b4513',
          boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
          width: '100%',
          minWidth: 'max-content',
          height: 'fit-content'
        }}>
        {/* Main Layout: Zero + Number Grid + Column Bets */}
        <div style={{ display: 'flex', gap: 3, width: '100%' }}>
          {/* Zero Section */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <button
              onClick={() => handlePlaceBet('number', 0)}
              disabled={spinning || result !== null}
              style={{
                width: 38,
                height: '100px',
                background: '#10b981',
                color: '#fff',
                border: '2px solid #047857',
                borderRadius: 4,
                fontSize: 18,
                fontWeight: 700,
                cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                position: 'relative',
                writingMode: 'vertical-rl',
                textOrientation: 'upright',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <span style={{ fontSize: 20, fontWeight: 700 }}>0</span>
              {getBetAmountForNumber(0) > 0 && (
                <span style={{ 
                  position: 'absolute', 
                  top: 8, 
                  right: 8, 
                  background: '#fbbf24', 
                  color: '#1f2937', 
                  padding: '4px 8px', 
                  borderRadius: 12, 
                  fontSize: 11, 
                  fontWeight: 700,
                  writingMode: 'horizontal-tb'
                }}>
                  €{getBetAmountForNumber(0)}
                </span>
              )}
            </button>
          </div>

          {/* Numbers Grid (3 rows x 12 columns) + 2 to 1 column + Betting Spots */}
          <div style={{ flex: 1, position: 'relative' }}>
            {/* Street Bet Spots (Left edge of each row) */}
            <div style={{ position: 'absolute', left: -12, top: 0, display: 'flex', flexDirection: 'column', gap: 1, zIndex: 10 }}>
              {[
                { row: [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36], label: '3rd', top: 0 },
                { row: [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35], label: '2nd', top: 33 },
                { row: [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34], label: '1st', top: 66 }
              ].map((street, idx) => (
                <button
                  key={`street-${idx}`}
                  onClick={() => handlePlaceBet('street', [street.row[0], street.row[1], street.row[2]])}
                  disabled={spinning || result !== null}
                  title={`Street: ${street.row[0]},${street.row[1]},${street.row[2]} (11:1)`}
                  style={{
                    width: 10,
                    height: 32,
                    background: getBetAmountForNumbers([street.row[0], street.row[1], street.row[2]]) > 0 ? '#fbbf24' : '#3b82f6',
                    border: '1px solid #fff',
                    borderRadius: 3,
                    cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                    position: 'relative',
                    padding: 0,
                    fontSize: 0,
                    transition: 'background 0.2s'
                  }}
                >
                  {getBetAmountForNumbers([street.row[0], street.row[1], street.row[2]]) > 0 && (
                    <span style={{
                      position: 'absolute',
                      left: -20,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: '#fbbf24',
                      color: '#1f2937',
                      padding: '2px 4px',
                      borderRadius: 6,
                      fontSize: 8,
                      fontWeight: 700,
                      whiteSpace: 'nowrap'
                    }}>
                      €{getBetAmountForNumbers([street.row[0], street.row[1], street.row[2]])}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Split & Corner Betting Overlay */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 5 }}>
              {/* This overlay contains clickable betting spots positioned absolutely */}
              {/* Horizontal Split Bets (between numbers in same row) */}
              {[
                // Row 3
                ...[[3, 6], [6, 9], [9, 12], [12, 15], [15, 18], [18, 21], [21, 24], [24, 27], [27, 30], [30, 33], [33, 36]].map((nums, idx) => ({
                  numbers: nums,
                  row: 0,
                  col: idx,
                  type: 'horizontal'
                })),
                // Row 2
                ...[[2, 5], [5, 8], [8, 11], [11, 14], [14, 17], [17, 20], [20, 23], [23, 26], [26, 29], [29, 32], [32, 35]].map((nums, idx) => ({
                  numbers: nums,
                  row: 1,
                  col: idx,
                  type: 'horizontal'
                })),
                // Row 1
                ...[[1, 4], [4, 7], [7, 10], [10, 13], [13, 16], [16, 19], [19, 22], [22, 25], [25, 28], [28, 31], [31, 34]].map((nums, idx) => ({
                  numbers: nums,
                  row: 2,
                  col: idx,
                  type: 'horizontal'
                }))
              ].map((split, idx) => {
                const betAmount = getBetAmountForNumbers(split.numbers);
                const leftPercent = (split.col + 0.5) / 12 * 100; // Position between two numbers
                const topPercent = split.row * 33.33 + 16.67; // Center of row
                
                return (
                  <button
                    key={`h-split-${idx}`}
                    onClick={() => handlePlaceBet('split', split.numbers)}
                    disabled={spinning || result !== null}
                    title={`Split: ${split.numbers.join('/')} (17:1)`}
                    style={{
                      position: 'absolute',
                      left: `${leftPercent}%`,
                      top: `${topPercent}%`,
                      transform: 'translate(-50%, -50%)',
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: 'transparent',
                      border: 'none',
                      cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                      padding: 0,
                      pointerEvents: 'auto',
                      transition: 'all 0.2s',
                      fontSize: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <span style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: betAmount > 0 ? '#fbbf24' : '#ef4444',
                      border: '2px solid #fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      opacity: betAmount > 0 ? 1 : 0.2,
                      transition: 'opacity 0.2s',
                      pointerEvents: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => {
                      if (betAmount === 0) e.currentTarget.style.opacity = '0.2';
                    }}
                    ></span>
                    {betAmount > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: -20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#fbbf24',
                        color: '#1f2937',
                        padding: '2px 4px',
                        borderRadius: 6,
                        fontSize: 8,
                        fontWeight: 700,
                        whiteSpace: 'nowrap'
                      }}>
                        €{betAmount}
                      </span>
                    )}
                  </button>
                );
              })}

              {/* Vertical Split Bets (between rows) */}
              {[
                // Between Row 3 and Row 2
                ...[...Array(12)].map((_, idx) => ({
                  numbers: [[3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36][idx], [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35][idx]],
                  row: 0.5,
                  col: idx
                })),
                // Between Row 2 and Row 1
                ...[...Array(12)].map((_, idx) => ({
                  numbers: [[2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35][idx], [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34][idx]],
                  row: 1.5,
                  col: idx
                }))
              ].map((split, idx) => {
                const betAmount = getBetAmountForNumbers(split.numbers);
                const leftPercent = (split.col + 0.5) / 12 * 100;
                const topPercent = split.row * 33.33;
                
                return (
                  <button
                    key={`v-split-${idx}`}
                    onClick={() => handlePlaceBet('split', split.numbers)}
                    disabled={spinning || result !== null}
                    title={`Split: ${split.numbers.join('/')} (17:1)`}
                    style={{
                      position: 'absolute',
                      left: `${leftPercent}%`,
                      top: `${topPercent}%`,
                      transform: 'translate(-50%, -50%)',
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: 'transparent',
                      border: 'none',
                      cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                      padding: 0,
                      pointerEvents: 'auto',
                      transition: 'all 0.2s',
                      fontSize: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <span style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: betAmount > 0 ? '#fbbf24' : '#ef4444',
                      border: '2px solid #fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      opacity: betAmount > 0 ? 1 : 0.2,
                      transition: 'opacity 0.2s',
                      pointerEvents: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => {
                      if (betAmount === 0) e.currentTarget.style.opacity = '0.2';
                    }}
                    ></span>
                    {betAmount > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: -20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#fbbf24',
                        color: '#1f2937',
                        padding: '2px 4px',
                        borderRadius: 6,
                        fontSize: 8,
                        fontWeight: 700,
                        whiteSpace: 'nowrap'
                      }}>
                        €{betAmount}
                      </span>
                    )}
                  </button>
                );
              })}

              {/* Corner Bets (at 4-number intersections) */}
              {[
                [1, 2, 4, 5], [2, 3, 5, 6], [4, 5, 7, 8], [5, 6, 8, 9], 
                [7, 8, 10, 11], [8, 9, 11, 12], [10, 11, 13, 14], [11, 12, 14, 15],
                [13, 14, 16, 17], [14, 15, 17, 18], [16, 17, 19, 20], [17, 18, 20, 21],
                [19, 20, 22, 23], [20, 21, 23, 24], [22, 23, 25, 26], [23, 24, 26, 27],
                [25, 26, 28, 29], [26, 27, 29, 30], [28, 29, 31, 32], [29, 30, 32, 33],
                [31, 32, 34, 35], [32, 33, 35, 36]
              ].map((numbers, idx) => {
                const betAmount = getBetAmountForNumbers(numbers);
                const col = idx % 11;
                const row = idx < 11 ? 0.5 : 1.5;
                const leftPercent = (col + 1) / 12 * 100;
                const topPercent = row * 33.33;
                
                return (
                  <button
                    key={`corner-${idx}`}
                    onClick={() => handlePlaceBet('corner', numbers)}
                    disabled={spinning || result !== null}
                    title={`Corner: ${numbers.join(',')} (8:1)`}
                    style={{
                      position: 'absolute',
                      left: `${leftPercent}%`,
                      top: `${topPercent}%`,
                      transform: 'translate(-50%, -50%)',
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: 'transparent',
                      border: 'none',
                      cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                      padding: 0,
                      pointerEvents: 'auto',
                      transition: 'all 0.2s',
                      fontSize: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <span style={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: betAmount > 0 ? '#fbbf24' : '#10b981',
                      border: '2px solid #fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      opacity: betAmount > 0 ? 1 : 0.2,
                      transition: 'opacity 0.2s',
                      pointerEvents: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => {
                      if (betAmount === 0) e.currentTarget.style.opacity = '0.2';
                    }}
                    ></span>
                    {betAmount > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: -20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#fbbf24',
                        color: '#1f2937',
                        padding: '2px 4px',
                        borderRadius: 6,
                        fontSize: 8,
                        fontWeight: 700,
                        whiteSpace: 'nowrap'
                      }}>
                        €{betAmount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(13, 1fr)', gap: 1 }}>
              {/* Row 3 (Top): 3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36 */}
              {[3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36].map(num => {
                const isRed = redNumbers.includes(num);
                const betAmount = getBetAmountForNumber(num);
                return (
                  <button
                    key={num}
                    onClick={() => handlePlaceBet('number', num)}
                    disabled={spinning || result !== null}
                    style={{
                      padding: '6px 4px',
                      background: isRed ? '#dc2626' : '#1f2937',
                      color: '#fff',
                      border: '1px solid #fff',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                      position: 'relative',
                      minHeight: 32
                    }}
                  >
                    {num}
                    {betAmount > 0 && (
                      <span style={{ 
                        position: 'absolute', 
                        top: 2, 
                        right: 2, 
                        background: '#fbbf24', 
                        color: '#1f2937', 
                        padding: '2px 6px', 
                        borderRadius: 10, 
                        fontSize: 9, 
                        fontWeight: 700,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                      }}>
                        €{betAmount}
                      </span>
                    )}
                  </button>
                );
              })}
              {/* 2 to 1 for Row 3 */}
              <button
                onClick={() => handlePlaceBet('col3', 'col3')}
                disabled={spinning || result !== null}
                style={{
                  padding: '4px',
                  background: '#1a5f3f',
                  color: '#fff',
                  border: '1px solid #fff',
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                  position: 'relative'
                }}
              >
                2 to 1
                {getBetAmountForType('col3') > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    background: '#fbbf24',
                    color: '#1f2937',
                    padding: '2px 6px',
                    borderRadius: 10,
                    fontSize: 8,
                    fontWeight: 700,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    €{getBetAmountForType('col3')}
                  </span>
                )}
              </button>
              
              {/* Row 2 (Middle): 2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35 */}
              {[2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35].map(num => {
                const isRed = redNumbers.includes(num);
                const betAmount = getBetAmountForNumber(num);
                return (
                  <button
                    key={num}
                    onClick={() => handlePlaceBet('number', num)}
                    disabled={spinning || result !== null}
                    style={{
                      padding: '6px 4px',
                      background: isRed ? '#dc2626' : '#1f2937',
                      color: '#fff',
                      border: '1px solid #fff',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                      position: 'relative',
                      minHeight: 32
                    }}
                  >
                    {num}
                    {betAmount > 0 && (
                      <span style={{ 
                        position: 'absolute', 
                        top: 2, 
                        right: 2, 
                        background: '#fbbf24', 
                        color: '#1f2937', 
                        padding: '2px 6px', 
                        borderRadius: 10, 
                        fontSize: 9, 
                        fontWeight: 700,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                      }}>
                        €{betAmount}
                      </span>
                    )}
                  </button>
                );
              })}
              {/* 2 to 1 for Row 2 */}
              <button
                onClick={() => handlePlaceBet('col2', 'col2')}
                disabled={spinning || result !== null}
                style={{
                  padding: '4px',
                  background: '#1a5f3f',
                  color: '#fff',
                  border: '1px solid #fff',
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                  position: 'relative'
                }}
              >
                2 to 1
                {getBetAmountForType('col2') > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    background: '#fbbf24',
                    color: '#1f2937',
                    padding: '2px 6px',
                    borderRadius: 10,
                    fontSize: 8,
                    fontWeight: 700,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    €{getBetAmountForType('col2')}
                  </span>
                )}
              </button>

              {/* Row 1 (Bottom): 1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34 */}
              {[1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34].map(num => {
                const isRed = redNumbers.includes(num);
                const betAmount = getBetAmountForNumber(num);
                return (
                  <button
                    key={num}
                    onClick={() => handlePlaceBet('number', num)}
                    disabled={spinning || result !== null}
                    style={{
                      padding: '6px 4px',
                      background: isRed ? '#dc2626' : '#1f2937',
                      color: '#fff',
                      border: '1px solid #fff',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                      position: 'relative',
                      minHeight: 32
                    }}
                  >
                    {num}
                    {betAmount > 0 && (
                      <span style={{ 
                        position: 'absolute', 
                        top: 2, 
                        right: 2, 
                        background: '#fbbf24', 
                        color: '#1f2937', 
                        padding: '2px 6px', 
                        borderRadius: 10, 
                        fontSize: 9, 
                        fontWeight: 700,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                      }}>
                        €{betAmount}
                      </span>
                    )}
                  </button>
                );
              })}
              {/* 2 to 1 for Row 1 */}
              <button
                onClick={() => handlePlaceBet('col1', 'col1')}
                disabled={spinning || result !== null}
                style={{
                  padding: '4px',
                  background: '#1a5f3f',
                  color: '#fff',
                  border: '1px solid #fff',
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                  position: 'relative'
                }}
              >
                2 to 1
                {getBetAmountForType('col1') > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    background: '#fbbf24',
                    color: '#1f2937',
                    padding: '2px 6px',
                    borderRadius: 10,
                    fontSize: 8,
                    fontWeight: 700,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    €{getBetAmountForType('col1')}
                  </span>
                )}
              </button>
            </div>

            {/* Bottom Betting Options */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(13, 1fr)', gap: 2, marginTop: 2 }}>
              {/* Dozens: 1st 12, 2nd 12, 3rd 12 */}
              <button
                onClick={() => handlePlaceBet('dozen1', 'dozen1')}
                disabled={spinning || result !== null}
                style={{
                  gridColumn: 'span 4',
                  padding: '6px',
                  background: '#1a5f3f',
                  color: '#fff',
                  border: '1px solid #fff',
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                  position: 'relative'
                }}
              >
                1st 12
                {getBetAmountForType('dozen1') > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    background: '#fbbf24',
                    color: '#1f2937',
                    padding: '2px 6px',
                    borderRadius: 10,
                    fontSize: 8,
                    fontWeight: 700,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    €{getBetAmountForType('dozen1')}
                  </span>
                )}
              </button>
              <button
                onClick={() => handlePlaceBet('dozen2', 'dozen2')}
                disabled={spinning || result !== null}
                style={{
                  gridColumn: 'span 4',
                  padding: '6px',
                  background: '#1a5f3f',
                  color: '#fff',
                  border: '1px solid #fff',
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                  position: 'relative'
                }}
              >
                2nd 12
                {getBetAmountForType('dozen2') > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    background: '#fbbf24',
                    color: '#1f2937',
                    padding: '2px 6px',
                    borderRadius: 10,
                    fontSize: 8,
                    fontWeight: 700,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    €{getBetAmountForType('dozen2')}
                  </span>
                )}
              </button>
              <button
                onClick={() => handlePlaceBet('dozen3', 'dozen3')}
                disabled={spinning || result !== null}
                style={{
                  gridColumn: 'span 4',
                  padding: '6px',
                  background: '#1a5f3f',
                  color: '#fff',
                  border: '1px solid #fff',
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                  position: 'relative'
                }}
              >
                3rd 12
                {getBetAmountForType('dozen3') > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    background: '#fbbf24',
                    color: '#1f2937',
                    padding: '2px 6px',
                    borderRadius: 10,
                    fontSize: 8,
                    fontWeight: 700,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    €{getBetAmountForType('dozen3')}
                  </span>
                )}
              </button>

              {/* 1-18, EVEN, RED, BLACK, ODD, 19-36 */}
              <button
                onClick={() => handlePlaceBet('1-18', '1-18')}
                disabled={spinning || result !== null}
                style={{
                  gridColumn: 'span 2',
                  padding: '6px',
                  background: '#1a5f3f',
                  color: '#fff',
                  border: '1px solid #fff',
                  fontSize: 9,
                  fontWeight: 600,
                  cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                  position: 'relative'
                }}
              >
                1 to 18
                {getBetAmountForType('1-18') > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    background: '#fbbf24',
                    color: '#1f2937',
                    padding: '2px 6px',
                    borderRadius: 10,
                    fontSize: 8,
                    fontWeight: 700,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    €{getBetAmountForType('1-18')}
                  </span>
                )}
              </button>
              <button
                onClick={() => handlePlaceBet('even', 'even')}
                disabled={spinning || result !== null}
                style={{
                  gridColumn: 'span 2',
                  padding: '6px',
                  background: '#1a5f3f',
                  color: '#fff',
                  border: '1px solid #fff',
                  fontSize: 9,
                  fontWeight: 600,
                  cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                  position: 'relative'
                }}
              >
                EVEN
                {getBetAmountForType('even') > 0 && (
                  <span style={{ position: 'absolute', top: 1, right: 1, background: '#fbbf24', color: '#1f2937', padding: '1px 4px', borderRadius: 8, fontSize: 8, fontWeight: 700 }}>
                    €{getBetAmountForType('even')}
                  </span>
                )}
              </button>
              <button
                onClick={() => handlePlaceBet('red', 'red')}
                disabled={spinning || result !== null}
                style={{
                  gridColumn: 'span 2',
                  padding: '6px',
                  background: '#dc2626',
                  color: '#fff',
                  border: '1px solid #fff',
                  fontSize: 9,
                  fontWeight: 600,
                  cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <div style={{ width: 14, height: 14, background: '#dc2626', border: '1px solid #fff', borderRadius: '50%' }}></div>
                {getBetAmountForType('red') > 0 && (
                  <span style={{ position: 'absolute', top: 1, right: 1, background: '#fbbf24', color: '#1f2937', padding: '1px 4px', borderRadius: 8, fontSize: 8, fontWeight: 700 }}>
                    €{getBetAmountForType('red')}
                  </span>
                )}
              </button>
              <button
                onClick={() => handlePlaceBet('black', 'black')}
                disabled={spinning || result !== null}
                style={{
                  gridColumn: 'span 2',
                  padding: '6px',
                  background: '#1f2937',
                  color: '#fff',
                  border: '1px solid #fff',
                  fontSize: 9,
                  fontWeight: 600,
                  cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <div style={{ width: 14, height: 14, background: '#1f2937', border: '1px solid #fff', borderRadius: '50%' }}></div>
                {getBetAmountForType('black') > 0 && (
                  <span style={{ position: 'absolute', top: 1, right: 1, background: '#fbbf24', color: '#1f2937', padding: '1px 4px', borderRadius: 8, fontSize: 8, fontWeight: 700 }}>
                    €{getBetAmountForType('black')}
                  </span>
                )}
              </button>
              <button
                onClick={() => handlePlaceBet('odd', 'odd')}
                disabled={spinning || result !== null}
                style={{
                  gridColumn: 'span 2',
                  padding: '6px',
                  background: '#1a5f3f',
                  color: '#fff',
                  border: '1px solid #fff',
                  fontSize: 9,
                  fontWeight: 600,
                  cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                  position: 'relative'
                }}
              >
                ODD
                {getBetAmountForType('odd') > 0 && (
                  <span style={{ position: 'absolute', top: 1, right: 1, background: '#fbbf24', color: '#1f2937', padding: '1px 4px', borderRadius: 8, fontSize: 8, fontWeight: 700 }}>
                    €{getBetAmountForType('odd')}
                  </span>
                )}
              </button>
              <button
                onClick={() => handlePlaceBet('19-36', '19-36')}
                disabled={spinning || result !== null}
                style={{
                  gridColumn: 'span 2',
                  padding: '6px',
                  background: '#1a5f3f',
                  color: '#fff',
                  border: '1px solid #fff',
                  fontSize: 9,
                  fontWeight: 600,
                  cursor: (!spinning && result === null) ? 'pointer' : 'not-allowed',
                  position: 'relative'
                }}
              >
                19 to 36
                {getBetAmountForType('19-36') > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    background: '#fbbf24',
                    color: '#1f2937',
                    padding: '2px 6px',
                    borderRadius: 10,
                    fontSize: 8,
                    fontWeight: 700,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    €{getBetAmountForType('19-36')}
                  </span>
                )}
              </button>
              {/* Empty space to align with 2 to 1 column */}
              <div style={{ gridColumn: 'span 1' }}></div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Betting Instructions */}
      <div style={{ background: '#fef3c7', padding: '12px 16px', borderRadius: 8, border: '1px solid #fbbf24' }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#92400e', margin: 0, textAlign: 'center' }}>
          🎰 <strong>How to Place Bets:</strong>
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: 8, fontSize: 11, color: '#92400e' }}>
          <div>• <strong>Numbers:</strong> Click directly on numbers (35:1)</div>
          <div>• <strong>Splits:</strong> Click faint red dots between numbers (17:1)</div>
          <div>• <strong>Streets:</strong> Click blue bars on left edge (11:1)</div>
          <div>• <strong>Corners:</strong> Click faint green dots at intersections (8:1)</div>
          <div>• <strong>Outside:</strong> Click red/black, odd/even, etc. (1:1)</div>
          <div>• <strong>Dozens/Columns:</strong> Click 1st-3rd 12 or 2:1 (2:1)</div>
        </div>
      </div>

      {/* Spin Button */}
      {!spinning && !result && (
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={spin}
            disabled={bets.length === 0}
            style={{
              padding: '16px 48px',
              background: bets.length > 0 ? branding.primaryColor : '#9ca3af',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              fontSize: 20,
              fontWeight: 700,
              cursor: bets.length > 0 ? 'pointer' : 'not-allowed',
              boxShadow: bets.length > 0 ? '0 4px 12px rgba(134, 25, 143, 0.4)' : 'none'
            }}
          >
            Spin (€{totalBetAmount.toFixed(2)})
          </button>
        </div>
      )}

      {/* Spinning Animation */}
      {spinning && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            width: 100,
            height: 100,
            border: '8px solid #e5e7eb',
            borderTop: '8px solid ' + branding.primaryColor,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }} />
          <p style={{ marginTop: 16, fontSize: 18, fontWeight: 600 }}>Spinning...</p>
        </div>
      )}

      {/* Result */}
      {result !== null && (
        <div style={{
          padding: '24px',
          background: '#f9fafb',
          borderRadius: 8,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>
            {redNumbers.includes(result) ? '🔴' : blackNumbers.includes(result) ? '⚫' : '🟢'}
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, marginBottom: 8 }}>{result}</div>
          {message && (
            <div style={{
              padding: '16px',
              background: message.includes('Winner') ? '#d1fae5' : '#fee2e2',
              borderRadius: 8,
              marginBottom: 16,
              marginTop: 16
            }}>
              {message.split('\n').map((line, idx) => (
                <div key={idx} style={{
                  fontSize: idx === 0 ? 20 : 14,
                  fontWeight: idx === 0 ? 700 : 600,
                  color: message.includes('Winner') ? '#065f46' : '#991b1b',
                  marginBottom: idx === 0 ? 8 : 0
                }}>
                  {line}
                </div>
              ))}
            </div>
          )}
          <button
            onClick={resetGame}
            style={{
              padding: '12px 24px',
              background: branding.primaryColor,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaRedo /> Play Again
          </button>
        </div>
      )}

      {/* Error Message */}
      {message && message.includes('Invalid') && (
        <div style={{
          padding: '12px',
          background: '#fee2e2',
          color: '#991b1b',
          borderRadius: 8,
          textAlign: 'center'
        }}>
          {message}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default GamePlayer;

